import { apiClient } from '../utils/api-client';
import i18next from 'i18next';
import type {
  ProfileResponse,
  ProfileUpdateRequest,
  ProfileUpdateResponse,
  PasswordUpdateRequest,
  PasswordUpdateResponse
} from '../types/user';

export class ProfileService {
  private static instance: ProfileService;
  private readonly CACHE_KEY = 'profile_cache';
  private readonly CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

  static getInstance(): ProfileService {
    if (!ProfileService.instance) {
      ProfileService.instance = new ProfileService();
    }
    return ProfileService.instance;
  }

  /**
   * Get current user profile with offline cache support
   */
  async getProfile(): Promise<ProfileResponse> {
    try {
      const response = await apiClient.get('/api/profile', {
        requireAuth: true
      });

      if (!response.ok) {
        // If request fails and we're offline, try cache
        if (!navigator.onLine) {
          return this.getCachedProfile();
        }
        throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Transform JSON:API response to expected format
      const transformedData = {
        user: {
          id: parseInt(data.data.id),
          ...data.data.attributes
        }
      };


      // Cache the successful response
      this.cacheProfile(transformedData);

      return transformedData;
    } catch (error) {
      // If we're offline or request failed, try cache
      if (!navigator.onLine || error instanceof TypeError) {
        return this.getCachedProfile();
      }
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(data: ProfileUpdateRequest): Promise<ProfileUpdateResponse> {

    const response = await apiClient.put('/api/profile', data, {
      requireAuth: true
    });

    if (!response.ok) {
      const errorData = await response.json();

      const error = new Error(errorData.message || `Failed to update profile: ${response.status}`);
      (error as unknown as { response?: string }).response = JSON.stringify(errorData);
      throw error;
    }

    const result = await response.json();

    // Check if this was an offline queued response (status 202)
    if (response.status === 202) {
      return {
        ...result,
        offline: true
      };
    }


    // Transform JSON:API response to expected format and update cache
    const transformedResult = {
      message: result.meta?.message || i18next.t('errors.profileUpdateSuccess'),
      user: {
        id: parseInt(result.data.id),
        ...result.data.attributes
      }
    };

    this.cacheProfile({ user: transformedResult.user });

    return transformedResult;
  }

  /**
   * Store the interface language on the account. Kept apart from updateProfile
   * because it travels alone, from the language switcher rather than the form:
   * emails and push notifications read this field, so it has to follow whatever
   * language the app is being read in.
   */
  async updateLanguage(language: string): Promise<void> {
    const response = await apiClient.put(
      '/api/profile',
      { user: { language } },
      { requireAuth: true }
    );

    if (!response.ok) {
      throw new Error(`Failed to update language: ${response.status}`);
    }

    // The cached profile still carries the old language.
    this.clearCache();
  }

  /**
   * Update user password
   */
  async updatePassword(data: PasswordUpdateRequest): Promise<PasswordUpdateResponse> {

    const response = await apiClient.patch('/api/profile/password', data, {
      requireAuth: true
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to update password: ${response.status}`);
    }


    const result = await response.json();
    return {
      message: result.meta?.message || i18next.t('errors.passwordUpdateSuccess')
    };
  }

  /**
   * Issue a fresh coach invite token, invalidating any link already shared.
   * Returns the updated profile (carrying the new coach_token).
   */
  async regenerateCoachToken(): Promise<ProfileResponse> {
    const response = await apiClient.post(
      '/api/profile/coach_token/regenerate',
      {},
      { requireAuth: true }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to regenerate coach token: ${response.status}`);
    }

    const result = await response.json();
    const transformed = {
      user: {
        id: parseInt(result.data.id),
        ...result.data.attributes,
      },
    };

    this.cacheProfile(transformed);
    return transformed;
  }

  /**
   * Cache profile data in localStorage
   */
  private cacheProfile(data: ProfileResponse): void {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
  }

  /**
   * Get cached profile data
   */
  private getCachedProfile(): ProfileResponse {
    const cached = localStorage.getItem(this.CACHE_KEY);

    if (!cached) {
      throw new Error('No cached profile data available');
    }

    const { data, timestamp } = JSON.parse(cached);

    // Check if cache is still valid
    if (Date.now() - timestamp > this.CACHE_EXPIRY) {
      localStorage.removeItem(this.CACHE_KEY);
      throw new Error('Cached profile data expired');
    }

    return data;
  }

  /**
   * Clear cached profile data
   */
  clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
  }
}

export const profileService = ProfileService.getInstance();
